import {
	Action,
	NgxsOnInit,
	Select,
	Selector,
	State,
	StateContext,
	Store,
} from '@ngxs/store';
import { IFeatureFlags } from './applicationDefaults.model';
import { Injectable } from '@angular/core';
import * as actions from './applicationDefaults.actions';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { DocumentPaths } from '@models/collections';
import LogRocket from 'logrocket';
import { Observable, of, Subscription } from 'rxjs';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import { filter, map, switchMap } from 'rxjs/operators';
import { SettingsData } from 'src/app/private/home/header-bar/header-bar.types';
import { AngularFireStorage } from '@angular/fire/compat/storage';

export interface FeatureFlagStateModel {
	connected: boolean;
	loading: boolean;
	featureFlags: IFeatureFlags | null;
	bannerURL: string | null;
}

@State<FeatureFlagStateModel>({
	name: 'featureFlags',
	defaults: {
		connected: false,
		loading: false,
		featureFlags: null,
		bannerURL: null,
	},
})
@Injectable()
export class FeatureFlagState implements NgxsOnInit {
	@Select(AuthState.getLoggedInUser) loggedInUser$: Observable<AuthUser>;

	constructor(
		private angularFs: AngularFirestore,
		private store: Store,
		private storage: AngularFireStorage,
	) {}
	ngxsOnInit() {
		this.loggedInUser$.pipe().subscribe((user) => {
			if (!user) {
				this.loggedOut();
				return;
			}
			this.store.dispatch(new actions.Get());
		});
	}

	featureFlagsSub: Subscription = null;
	bannerUrlSub: Subscription = null;

	@Selector()
	static getFeatureFlags(state: FeatureFlagStateModel): IFeatureFlags | null {
		return state.featureFlags;
	}

	@Selector()
	static getBannerUrl(state: FeatureFlagStateModel): string | null {
		return state.bannerURL;
	}

	loggedOut(): void {
		this.featureFlagsSub?.unsubscribe();
		this.bannerUrlSub?.unsubscribe();
		this.featureFlagsSub = null;
		this.bannerUrlSub = null;
	}

	@Action(actions.Get)
	get(ctx: StateContext<FeatureFlagStateModel>): void {
		const docRef = this.angularFs.doc(
			DocumentPaths.applicationDefaultsFeatureFlags(),
		);

		const bannerDocRef = this.angularFs.doc(
			DocumentPaths.applicationDefaults_closer(),
		);

		this.featureFlagsSub?.unsubscribe();
		this.bannerUrlSub?.unsubscribe();

		this.featureFlagsSub = docRef.snapshotChanges().subscribe(
			(data) => {
				if (data && data.payload.exists) {
					ctx.patchState({
						featureFlags: (data.payload.data() || {}) as IFeatureFlags,
						connected: true,
					});
				} else {
					ctx.patchState({
						featureFlags: {} as IFeatureFlags,
						connected: true,
					});
				}
			},
			(error) => {
				LogRocket.captureException(error);
				console.error('Error fetching feature flags:', error);
				ctx.patchState({
					featureFlags: {} as IFeatureFlags,
					connected: true,
				});
			},
		);

		this.bannerUrlSub = bannerDocRef
			.snapshotChanges()
			.pipe(
				filter((snapshot) => snapshot.payload.exists),
				map((snapshot) => snapshot.payload.data() as SettingsData),
				switchMap((settingsData) =>
					settingsData.banner?.path
						? this.storage.ref(settingsData.banner.path).getDownloadURL()
						: of(null),
				),
			)
			.subscribe((url) => {
				ctx.patchState({
					bannerURL: url,
				});
			});
	}
}
